<script setup lang="ts">
import { ref, computed } from "vue";
import { asyncComputed } from '@vueuse/core'
import { useTranslation } from "i18next-vue";

import type { TabItem } from "@/types/ui";

import { getIconName } from "@/mixins/icon";

import TabMenu from "@/components/ui/TabMenu.vue";
import EditActivitySettings from "@/components/activity/EditActivitySettings.vue";
import EditActivitySequences from "@/components/activity/EditActivitySequences.vue";
import EditActivityInterfaces from "@/components/activity/EditActivityInterfaces.vue";

const { t } = useTranslation();

const props = defineProps({
  activity_id: {
    type: String,
    required: true,
  },
});

const defaultTabItem = {
  icon: 'fa-light fa-gear',
  label: t('tab.settings'),
  value: 'settings',
}

const tabItems = asyncComputed(async() => {
  const iconList = await getIconName('fa-list-timeline');
  const iconGrid = await getIconName('fa-grid-2');
  
  return [
    {
      icon: 'fa-light fa-gear',
      label: t('tab.settings'),
      value: 'settings',
    },
    {
      icon: `fa-light ${iconList}`,
      label: t('tab.sequences'),
      value: 'sequences',
    },
    {
      icon: 'fa-light fa-icons',
      label: t('tab.user_interface'),
      value: 'user-interface',
    },
    {
      icon: `fa-light ${iconGrid}`,
      label: t('tab.button_mapping'),
      value: 'button-mapping',
    },
  ]
});

const activeTab = ref<TabItem>(defaultTabItem);
const slideRight = ref(false);

const tabTransition = computed(() => {
  return slideRight.value == true ? 'slide-tab-right' : 'slide-tab-left';
});

function goTo(item: TabItem) {
  const activeTabIndex = tabItems.value.findIndex(i => i.value == activeTab.value.value);
  const newTabIndex = tabItems.value.findIndex(i => i.value == item.value);
  slideRight.value = newTabIndex < activeTabIndex ? true : false;
  activeTab.value = item;
}

</script>
<template>
  <Suspense>
    <div class="page-edit-activity">
      <div class="page-edit-activity__navbar">
        <TabMenu
          v-if="tabItems"
          @item-click="goTo"
          :list-data="tabItems"
          :active-tab="activeTab"
        />
      </div>
      <div class="page-edit-activity__main">
        <div class="page-edit-activity__tabs">
          <Transition :name="tabTransition">
            <div v-show="activeTab.value == 'settings'" class="page-edit-activity__tab">
              <EditActivitySettings
                :activity-id="activity_id"
                :active="activeTab.value == 'settings'"
              />
            </div>
          </Transition>
          <Transition :name="tabTransition">
            <div v-show="activeTab.value == 'sequences'" class="page-edit-activity__tab">
              <EditActivitySequences
                :activity-id="activity_id"
                :active="activeTab.value == 'sequences'"
              />
            </div>
          </Transition>
          <Transition :name="tabTransition">
            <div v-show="activeTab.value == 'user-interface' || activeTab.value == 'button-mapping'" class="page-edit-activity__tab">
              <EditActivityInterfaces
                :activity-id="activity_id"
                :active-tab="activeTab.value"
              />
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </Suspense>
</template>
